import { FC } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  PieChartProps,
} from "recharts";
import { ChartContainer } from "@/components/ui/chart-container";
import { formatCurrency } from "@/utils/format";

interface BasePieChartProps extends PieChartProps {
  data: Array<{ name: string; value: number }>; // Data format with name and value keys
  colors: string[]; // Colors for the pie slices
  formatter?: (value: number | string) => string; // Formatter for values in tooltips
  legendTitle?: string; // Optional title for the legend
  outerRadius?: number; // Optional outer radius for the pie chart
  showPercentage?: boolean; // Show percentage in labels instead of value
}

export const BasePieChart: FC<BasePieChartProps> = ({
  data,
  colors,
  formatter = formatCurrency,
  legendTitle = "Legend",
  outerRadius = 100,
  showPercentage = false,
  ...restProps
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const formatLabel = ({ name, value }: { name: string; value: number }) => {
    if (showPercentage && total > 0) {
      const percentage = ((value / total) * 100).toFixed(1);
      return `${name}: ${percentage}%`;
    }
    return `${name}: ${formatter(value)}`;
  };

  return (
    <ChartContainer aria-label="Pie chart visualization">
      <ResponsiveContainer>
        <PieChart {...restProps} aria-label="Responsive pie chart">
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={outerRadius}
            label={formatLabel}
            aria-label="Pie chart slice"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${entry.name}`}
                fill={colors[index % colors.length]} // Cycle through colors
                aria-label={`Slice for ${entry.name}, value: ${formatter(entry.value)}`}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number | string) => formatter(value)}
            labelFormatter={(label) => `Category: ${label}`} // Add context to labels
          />
          <Legend
            payload={data.map((entry, index) => ({
              id: entry.name,
              type: "square",
              value: `${entry.name}`,
              color: colors[index % colors.length],
            }))}
          >
            <div aria-label="Legend title">{legendTitle}</div>
          </Legend>
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};
